import cx from 'clsx';
import React, { memo } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';

import { getNodeSensors } from '@helpers/getNodeSensors';
import { calcSensorHex } from '@helpers/helper';

import useTranslation from '@hooks/useTranslation';

import type { IActionItem } from '@model/actionItem';

import NodeSensorsSkeleton from './NodeSensorsSkeleton';
import style from './style.module.scss';

interface IProps {
  actionItem?: IActionItem;
}

const NodeSensors: React.FC<IProps> = ({ actionItem }) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();

  const { customerId } = useParams();

  const sensors = actionItem?.node?.activeWwrSensors || [];
  const locationName = actionItem?.location_info || '-';

  const nodeSensors = getNodeSensors(sensors);

  if (!actionItem) return <NodeSensorsSkeleton />;

  if (!(actionItem?.equipment_type === 'node' && sensors.length)) {
    return null;
  }

  return (
    <div className={style.block}>
      <div className={style.blockHead}>
        <div className={style.blockTitle}>{t('Node Sensors')}</div>
      </div>
      <div className={style.blockContainer}>
        <div className={style.information}>
          {t(
            'The following sensors are attached to this node. We provide this information to assist you in locating the node. Please note the EAM number ("Equipment") and Bed number ("Alias").',
          )}
          {t(
            'Please also confirm that the Node Serial Number matches the serial number listed in the header of this Action Item.',
          )}
        </div>
        <table className={style.sensorsTable} cellSpacing="0" cellPadding="0">
          <thead className={style.sensorsTableHead}>
            <tr>
              <th className={style.sensorsTableTh}>{t('Port')}</th>
              <th className={style.sensorsTableTh}>{t('Sensor HEX code')}</th>
              <th className={style.sensorsTableTh}>{t('Equipment')}</th>
              <th className={style.sensorsTableTh}>{t('Location')}</th>
            </tr>
          </thead>
          <tbody className={style.sensorsTableBody}>
            {nodeSensors.map((sensor, index) =>
              !sensor ? (
                <tr key={index} className={style.sensorsTableBodyRow}>
                  <td className={style.sensorsTableTd}>{index + 1}</td>
                  <td className={cx(style.sensorsTableTd, style.noSensorText)} colSpan={3}>
                    {t('No connected sensor')}
                  </td>
                </tr>
              ) : (
                <tr key={index} className={style.sensorsTableBodyRow}>
                  <td className={style.sensorsTableTd}>{sensor.position}</td>
                  <td className={style.sensorsTableTd}>
                    {sensor.installationPoint.component !== 'Tachometer'
                      ? calcSensorHex(sensor.sensor_id, sensor.hardware_id)
                      : ''}
                  </td>
                  <td className={style.sensorsTableTd}>
                    <Link
                      to={`/customer/${customerId}/sensors/${sensor.installationPoint?.equipment_id}/list`}
                      state={{
                        prevPage: pathname,
                      }}
                      className={style.link}
                    >
                      {sensor.installationPoint.component !== 'Tachometer'
                        ? sensor.installationPoint.equipment?.name
                        : ''}
                    </Link>
                  </td>
                  <td className={style.sensorsTableTd}>{locationName}</td>
                </tr>
              ),
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default memo(NodeSensors);
